import { useEffect, useMemo } from 'react';
import { Award, BookOpen, MapPin, Sparkles, Users } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import type { Option } from '@/constants/institute';
import type { AllocatedClass } from '../types/teacher';
import { allotmentLabel, formatInstituteLongDate, instituteToday } from '../utils';

interface ClassSelectorHeaderProps {
  teacherName: string;
  email: string;
  allocatedClasses: AllocatedClass[];
  selectedClassId: string;
  onSelectClass: (id: string) => void;
}

export function ClassSelectorHeader({
  teacherName,
  email,
  allocatedClasses,
  selectedClassId,
  onSelectClass,
}: ClassSelectorHeaderProps) {
  const currentClass = allocatedClasses.find((c) => c.id === selectedClassId) || allocatedClasses[0];

  const classOptions = useMemo<Option[]>(
    () =>
      allocatedClasses.map((ac) => ({
        value: ac.id,
        label: allotmentLabel(ac),
      })),
    [allocatedClasses],
  );

  // selectedClassId starts as '' in the parent and is never seeded when
  // allocations first arrive. Promote the first class as soon as options exist
  // so the dropdown always shows a label instead of being blank.
  useEffect(() => {
    if (!selectedClassId && allocatedClasses.length > 0) {
      onSelectClass(allocatedClasses[0].id);
    }
  }, [selectedClassId, allocatedClasses, onSelectClass]);

  // The school's today, not the device's — a phone in another zone, or one
  // read just after midnight UTC, would otherwise head the page with the wrong day.
  const formattedDate = formatInstituteLongDate(instituteToday());

  return (
    <div className="relative overflow-hidden rounded-3xl border border-blue-100 dark:border-blue-900/40 bg-[#eff6ff] dark:bg-[#172554]/40 px-5 py-3 sm:px-7 sm:py-4 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)] print:hidden">
      {/* Ambient background lights and sheen */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-28 h-64 w-64 rounded-full bg-gradient-to-br from-blue-400/30 to-indigo-500/20 blur-3xl animate-orb"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-28 right-1/4 h-56 w-56 rounded-full bg-gradient-to-br from-sky-300/25 to-blue-400/10 blur-3xl animate-orb-slow"
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen dark:via-white/10" />
      </div>

      <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        {/* Left Profile Details */}
        <div className="flex items-center gap-4 min-w-0">
          <div className="relative shrink-0">
            <span
              className="relative grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-2xl border border-blue-200/70 dark:border-blue-800/50 bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-xl font-bold text-white shadow-md shadow-blue-500/25 sm:h-[72px] sm:w-[72px]"
              aria-hidden
            >
              {teacherName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
              <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen" />
            </span>
            <span
              className="absolute -bottom-0.5 -right-0.5 grid h-5 w-5 place-items-center rounded-full bg-ok text-white text-2xs font-bold shadow-xs ring-2 ring-white dark:ring-slate-900"
              title="Active"
            >
              ✓
            </span>
          </div>

          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-[11px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-blue-500" /> Teacher Portal
            </span>
            <h1 className="text-xl font-black tracking-tight text-slate-900 dark:text-white sm:text-2xl leading-tight">
              Hello, {teacherName}
            </h1>
            <p className="text-xs font-medium text-slate-600 dark:text-slate-300 sm:text-sm">
              {email} <span className="text-slate-400">·</span> {formattedDate}
            </p>
          </div>
        </div>

        {/* Right Active Class Selector Box */}
        <div className="flex flex-col gap-2 w-full lg:w-auto lg:min-w-[340px] shrink-0 bg-white/80 dark:bg-slate-900/70 backdrop-blur-md p-4 rounded-2xl border border-blue-100 dark:border-blue-900/50 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
              Active Class &amp; Subject
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold">
              {allocatedClasses.length} Allocated
            </span>
          </div>

          <Select
            label="Active class and subject"
            hideLabel
            options={classOptions}
            value={selectedClassId}
            onChange={(e) => onSelectClass(e.target.value)}
            className="h-11 px-4 py-2.5 text-xs sm:text-sm font-semibold rounded-field"
          />
        </div>
      </div>

      {/* Class Details Strip */}
      {currentClass && (
        <div className="relative z-10 mt-3 pt-3 border-t border-blue-200/60 dark:border-blue-800/40 flex flex-wrap items-center gap-2.5 text-xs">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200/70 dark:border-blue-800/50 bg-white/70 dark:bg-slate-900/50 px-3.5 py-1.5 font-bold text-slate-700 dark:text-slate-200 backdrop-blur-sm shadow-2xs">
            <BookOpen className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400 shrink-0" />
            Class: <span className="text-blue-700 dark:text-blue-300 text-sm font-bold ml-0.5">{currentClass.className}</span>
          </span>

          <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200/70 dark:border-blue-800/50 bg-white/70 dark:bg-slate-900/50 px-3.5 py-1.5 font-bold text-slate-700 dark:text-slate-200 backdrop-blur-sm shadow-2xs">
            Subject:{' '}
            <span className="text-blue-700 dark:text-blue-300 text-sm font-bold ml-0.5">
              {currentClass.subject ?? 'Class teacher only'}
            </span>
          </span>

          {currentClass.roomNo && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200/70 dark:border-blue-800/50 bg-white/70 dark:bg-slate-900/50 px-3.5 py-1.5 font-bold text-slate-700 dark:text-slate-200 backdrop-blur-sm shadow-2xs">
              <MapPin className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400 shrink-0" />
              Room: <span className="text-blue-700 dark:text-blue-300 text-sm font-bold ml-0.5">{currentClass.roomNo}</span>
            </span>
          )}

          <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200/80 dark:border-indigo-800/60 bg-indigo-50/80 dark:bg-indigo-950/50 px-3.5 py-1.5 font-bold text-indigo-700 dark:text-indigo-300 backdrop-blur-sm shadow-2xs">
            <Award className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400 shrink-0" /> {currentClass.role}
          </span>

          <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200/70 dark:border-blue-800/50 bg-white/70 dark:bg-slate-900/50 px-3.5 py-1.5 font-bold text-slate-700 dark:text-slate-200 backdrop-blur-sm shadow-2xs sm:ml-auto">
            <Users className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400 shrink-0" />
            <span className="text-blue-700 dark:text-blue-300 text-sm font-bold mr-0.5">{currentClass.totalStudents}</span> Students Enrolled
          </span>
        </div>
      )}
    </div>
  );
}
